import { create } from "zustand"

// Pop-out-window state for the quotation editor (#1430).
//
// Lives in a store for the same reason splitScreen.store does: the dual-view menu is rendered
// inside the Apryse shadow DOM by `applyViewerHeaderAndTools`, whose `render()` callback captures
// its props ONCE. A store subscription is what keeps the menu's rows live — which row exists
// (`Dual view in new tab` vs `Return right view back`) and which are disabled both depend on
// whether a pop-out is currently up. `usePopOutWindow` is the only writer.
type Store = {
	// Whether a pop-out window is currently open. In the OPENER this is the real answer; in the
	// CHILD it is set true on boot, because the child is itself the pop-out — that is what puts its
	// own menu into the popped-out state (`Return view back`, dual view inert).
	//
	// Polled as well as pushed: a window closed by the user's own title-bar X fires nothing we can
	// subscribe to cross-window, so the opener watches `window.closed` on top of the control
	// channel's `pop-out-closed` note (see usePopOutWindow).
	isPopOutOpen: boolean
	// True between `window.open` and the window being confirmed, so the row can't be double-picked
	// into opening two windows.
	isPopOutPending: boolean
	// Whether opening one is possible right now (editor mode, document loaded, ids known, feature
	// flag on, and not already inside the pop-out).
	isPopOutAvailable: boolean
	setPopOutOpen: (open: boolean) => void
	setPopOutPending: (pending: boolean) => void
	setPopOutAvailable: (available: boolean) => void
}

export const usePopOutWindowStore = create<Store>((set) => ({
	isPopOutOpen: false,
	isPopOutPending: false,
	isPopOutAvailable: false,
	// Settling the open flag EITHER WAY ends the pending state: pending means "we asked and don't
	// know yet", and both "it opened" and "it didn't" are answers. Resolving that here rather than at
	// each call site is what stops a blocked pop-up, or a close that races the poll, leaving the row
	// disabled for the rest of the session — the state the row can least afford, since with the
	// window gone there is nothing else to click.
	setPopOutOpen: (isPopOutOpen) => set({ isPopOutOpen, isPopOutPending: false }),
	setPopOutPending: (isPopOutPending) => set({ isPopOutPending }),
	setPopOutAvailable: (isPopOutAvailable) => set({ isPopOutAvailable }),
}))
